/**
 * KAN-858: переписка в карточке клиента приложения менеджера: кто ведёт и
 * кнопки ручного режима, вся лента с подгрузкой ранних и автообновлением,
 * поле ввода кабинета (MessageComposer: текст и фото) прижато к низу экрана
 * над вкладками. "С аккаунта: ..." показывает наш аккаунт отправки, при
 * нескольких аккаунтах выбор. Логика в useMobileClientFeed и useMobileManualChat.
 * KAN-940: недописанное сообщение хранится черновиком клиента (draftKey client:<id>).
 */
import { MessageComposer } from '@/components/messages/MessageComposer';
import { MobileChatControl } from '@/components/mobile/MobileChatControl';
import { MobileChatFeed } from '@/components/mobile/MobileClientFeed';
import { NativeSelect } from '@/components/ui/native-select';
import { useMobileClientFeed } from '@/hooks/useMobileClientFeed';
import { useMobileManualChat } from '@/hooks/useMobileManualChat';
import { clientMessageDraftKey } from '@/lib/composerDraft';
import { MOBILE_COMPOSER_ID, TAKE_AND_SEND_LABEL, TAKE_OVER_CONFIRM_TEXT } from '@/lib/mobileChat';
import type { Client } from '@/types/client';

const BTN = 'min-h-[44px] rounded-xl px-4 text-sm font-medium';

export function MobileClientChat({ client }: { client: Client }): JSX.Element {
  const feed = useMobileClientFeed(client.id);
  const chat = useMobileManualChat(client, feed.messages);

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="mobile-client-chat">
      <h2 className="text-base font-semibold text-gray-900">Переписка</h2>
      <MobileChatControl chat={chat} />
      <MobileChatFeed feed={feed} clientId={client.id} />
      {chat.canWrite && (
        <div
          id={MOBILE_COMPOSER_ID}
          className="sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-10 -mx-4 -mb-4 space-y-2 rounded-b-xl bg-white px-4 pb-3"
        >
          {chat.accountOptions.length > 1 && chat.sendOptionKey ? (
            <label className="flex items-center gap-2 pt-2 text-xs text-gray-600" data-testid="mobile-chat-account-picker">
              <span className="shrink-0">С аккаунта:</span>
              <NativeSelect
                className="h-9 w-auto min-w-[10rem]"
                value={chat.sendOptionKey}
                onChange={(event) => chat.chooseAccount(event.target.value)}
                options={chat.accountOptions.map((option) => ({ value: option.key, label: option.label }))}
                aria-label="С аккаунта"
              />
            </label>
          ) : (
            chat.sendFromText && (
              <p className="pt-2 text-xs text-gray-600" data-testid="mobile-chat-account">
                С аккаунта: <span className="text-gray-900">{chat.sendFromText}</span>
              </p>
            )
          )}
          {chat.takeAsked && (
            <div className="space-y-2 rounded-lg bg-amber-50 p-3" role="alertdialog" aria-label="Взять переписку">
              <p className="text-sm text-amber-900">{TAKE_OVER_CONFIRM_TEXT}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => void chat.confirmTake()}
                  className={`${BTN} bg-blue-600 text-white`}
                  data-testid="mobile-chat-take-confirm"
                >
                  {TAKE_AND_SEND_LABEL}
                </button>
                <button
                  type="button"
                  onClick={chat.cancelTake}
                  className={`${BTN} border border-gray-300 bg-white text-gray-900`}
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
          <MessageComposer
            onSend={chat.send}
            isSending={chat.isSending || chat.busy}
            draftKey={clientMessageDraftKey(client.id)}
          />
        </div>
      )}
    </section>
  );
}
